import { Input, Button, Form, DatePicker, Tooltip } from "antd";
import Dropdown from "../../components/search/Dropdown";
import React, { useState, useRef, useEffect } from "react";
import GlobalExportButton from "./export/GlobalExportButton";
import GlobalExportHandler from "./export/GlobalExportHandler";
import { validateName, validateSpecialChars } from "../utils/ValidationUtils";
import searchPageLabels from "../search/searchPageLabels.json";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { searchEmployees, fetchDataForDropdown } from "../../services/Service";

const SearchBar = ({ data, onSearch, onReset}, ref) => {
  const { labels, placeholders, buttons, other, tooltip, validationMessages } =
    searchPageLabels;

  // Create the form instance
  const [form] = Form.useForm();
  const [filteredData, setFilteredData] = useState(data);
  const [isFiltered, setIsFiltered] = useState(false);
  // Disable search button condition
  const [isSearchDisabled, setIsSearchDisabled] = useState(true);
  const searchInputRef = useRef(null);
  const token = useSelector((state) => state.auth.token);

  const initialFilters = {
    uid: "",
    firstName: "",
    lastName: "",
    location: "",
    status: "",
    startDate: null,
    endDate: null,
  };

  // Function to reset form fields
  const handleReset = () => {
    // Reset to original data
    form.resetFields();
    setFilteredData(data);
    setIsFiltered(false);
    setIsSearchDisabled(true);
    resetFilters();
    if (onReset) {
      onReset();
    }
    // Focus the search bar after reset
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const [filters, setFilters] = useState(initialFilters);

  const handleFilterChange = (key, value, form) => {
    if (form && form.validateFields) {
      form.validateFields([key]);
    }
    setFilters({ ...filters, [key]: value });
  };
  const resetFilters = () => {
    setFilters(initialFilters);
  };
  const checkSearchButtonStatus = () => {
    // Check if at least one field is filled and valid
    const isFormValid = Object.values(filters).some(
      (value) => value !== "" && value !== null
    );

    const isValidUID = validateSpecialChars(filters.uid);
    const isValidName =
      validateName(filters.firstName) && validateName(filters.lastName);

    // Enable the search button only if all fields are valid
    if (isFormValid && isValidUID && isValidName) {
      setIsSearchDisabled(false);
    } else {
      setIsSearchDisabled(true);
    }
  };

  // Hook to watch for filter changes and enable/disable the search button accordingly
  useEffect(() => {
    checkSearchButtonStatus();
  }, [filters]);

  const handleSearch = async () => {
    try {
      await form.validateFields();

      const formattedStartDate = filters.startDate
        ? dayjs(filters.startDate.$d).format("DD-MM-YYYY")
        : undefined;

      const formattedEndDate = filters.endDate
        ? dayjs(filters.endDate.$d).format("DD-MM-YYYY")
        : undefined;

      // Prepare query params
      const queryParams = {
        uid: filters.uid || undefined,
        firstName: filters.firstName || undefined,
        lastName: filters.lastName || undefined,
        location: filters.location !== "All" ? filters.location : undefined,
        status: filters.status !== "All" ? filters.status : undefined,
        onBoardingStartDate: formattedStartDate || undefined,
        onBoardingEndDate: formattedEndDate || undefined,
      };
      console.log(" filters.startDate", formattedStartDate);
      // Remove undefined values from queryParams
      Object.keys(queryParams).forEach(
        (key) => queryParams[key] === undefined && delete queryParams[key]
      );

      const result = await searchEmployees(queryParams, token);
      console.log(result);
      setFilteredData(result);
      setIsFiltered(true);

      onSearch(result);
    } catch (error) {
      console.error("Search failed:", error);
    }
  };

  const handleGlobalExport = () => {
    GlobalExportHandler(data, isFiltered, filteredData);
  };

  return (
    <div className="bg-white h-[350px] text-white flex flex-col p-4 rounded-md shadow-lg max-w-[1050px] mx-auto">
      <div className="mb-4 text-2xl font-semibold text-center border bg-[var(--color-primary)]  rounded-tl-[25px] rounded-tr-[25px]">
        <span className="inline-block h-8 align-middle mt-2">
          Employee Finder
        </span>
      </div>
      <Form form={form} layout="vertical">
        <Form.Item name="globalInfo" className="mt-[-6px]">
          <span className="text-gray-500 italic">{other.infoMessage}</span>
        </Form.Item>
        <div className="grid grid-cols-3 gap-x-6 mt-[-22px]">
          <Form.Item
            name="uid"
            label={labels.uid}
            labelCol={{ className: "text-sm font-medium" }}
            rules={[{ validator: validateSpecialChars }]}
          >
            <Input
              ref={searchInputRef}
              className="border py-1 w-70"
              placeholder={placeholders.uid}
              onChange={(e) => handleFilterChange("uid", e.target.value, form)}
            />
          </Form.Item>
          <Form.Item
            name="firstName"
            label={labels.firstName}
            labelCol={{ className: "text-sm font-medium" }}
            rules={[{ validator: validateName }]}
          >
            <Input
              className="border  py-1 w-70"
              placeholder={placeholders.firstName}
              onChange={(e) =>
                handleFilterChange("firstName", e.target.value, form)
              }
            />
          </Form.Item>
          <Form.Item
            name="lastName"
            label={labels.lastName}
            labelCol={{ className: "text-sm font-medium" }}
            rules={[{ validator: validateName }]}
          >
            <Input
              className="border p-2 py-1 w-70"
              placeholder={placeholders.lastName}
              onChange={(e) =>
                handleFilterChange("lastName", e.target.value, form)
              }
            />
          </Form.Item>
          <Form.Item
            name="location"
            label={labels.location}
            labelCol={{ className: "text-sm font-medium" }}
          >
            <Dropdown
              fetchData={() => fetchDataForDropdown("locations")}
              placeholder={placeholders.location}
              onChange={(value) =>
                handleFilterChange("location", value === "ALL" ? "All" : value)
              }
              includeAll={true}
            />
          </Form.Item>
          <Form.Item
            name="onboardingDate"
            label={labels.onboardingDate}
            labelCol={{ className: "text-sm font-medium" }}
          >
            <div className="flex gap-4 h-fit">
              <Form.Item
                className="mb-0 p-0 flex items-start"
                name="startDate"
                rules={[
                  {
                    validator: (_, value) =>
                      !filters.endDate || value
                        ? Promise.resolve()
                        : Promise.reject(validationMessages.validateStartDate),
                  },
                ]}
              >
                <DatePicker
                  placeholder={placeholders.onboardingDateStart}
                  format="DD-MM-YYYY"
                  className="w-25"
                  inputReadOnly={true}
                  disabledDate={(current) => current && current > new Date()}
                  onChange={(date) => handleFilterChange("startDate", date)}
                />
              </Form.Item>
              <Form.Item
                className="mb-0 p-0 flex items-start"
                name="endDate"
                rules={[
                  {
                    validator: (_, value) =>
                      value &&
                      filters.startDate &&
                      value.isBefore(filters.startDate)
                        ? Promise.reject(
                            validationMessages.validateStartEndDate
                          )
                        : Promise.resolve(),
                  },
                ]}
              >
                <DatePicker
                  placeholder={placeholders.onboardingDateEnd}
                  format="DD-MM-YYYY"
                  className="w-25"
                  inputReadOnly={true}
                  disabledDate={(current) =>
                    (current && current > new Date()) ||
                    current.isBefore(filters.startDate)
                  }
                  onChange={(date) => handleFilterChange("endDate", date)}
                />
              </Form.Item>
            </div>
          </Form.Item>
          <Form.Item
            name="status"
            label={labels.status}
            labelCol={{ className: "text-sm font-medium" }}
          >
            <Dropdown
              fetchData={() => fetchDataForDropdown("status")} 
              placeholder={placeholders.status}
              onChange={(value) =>
                handleFilterChange("status", value === "ALL" ? "All" : value)
              }
              includeAll={true}
            />
          </Form.Item>
        </div>
        <div className="flex justify-center gap-4">
          <Tooltip
            title={
              isSearchDisabled ? tooltip.searchDisabled : tooltip.searchEnabled
            }
          >
            <Button
              className="text-sm font-medium bg-[#183D5D] text-white rounded-full px-6 py-4 flex items-center gap-4 shadow-xl hover:bg-[#4A90E2]"
              type="primary"
              onClick={handleSearch}
              disabled={isSearchDisabled}
            >
              {buttons.search}
            </Button>
          </Tooltip>
          <Button
            className="text-sm font-medium bg-[#183D5D] text-white rounded-full px-6 py-4 flex items-center gap-4 shadow-xl hover:bg-[#4A90E2]"
            type="default"
            onClick={handleReset}
          >
            {buttons.reset}
          </Button>
        </div>
      </Form>
    </div>
  );
};
export default React.forwardRef(SearchBar);
